import React, { useRef, useState } from "react";
import { AgreementMemberInfo } from "../data/types";

export type AgreementSignatureSectionProps = {
  memberInfo: AgreementMemberInfo;
  setMemberInfo: React.Dispatch<React.SetStateAction<AgreementMemberInfo>>;
  variant?: "desktop" | "mobile";
  /** When true, only the draw modal is rendered (trigger lives in agreement HTML). */
  hideTrigger?: boolean;
  modalOpen?: boolean;
  onModalOpenChange?: (open: boolean) => void;
};

const AgreementSignatureSection: React.FC<AgreementSignatureSectionProps> = ({
  memberInfo,
  setMemberInfo,
  variant = "desktop",
  hideTrigger = false,
  modalOpen,
  onModalOpenChange,
}) => {
  const [showSignature, setShowSignature] = useState(false);
  const isModalOpen = hideTrigger ? Boolean(modalOpen) : showSignature;
  const setModalOpen = hideTrigger
    ? (open: boolean) => onModalOpenChange?.(open)
    : setShowSignature;
  const signatureCanvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const isMobile = variant === "mobile";

  const getSignatureCoords = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = "touches" in e ? e.touches[0]?.clientX : (e as React.MouseEvent).clientX;
    const clientY = "touches" in e ? e.touches[0]?.clientY : (e as React.MouseEvent).clientY;
    if (clientX == null || clientY == null) return null;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const getSignatureCoordsEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !e.changedTouches?.length) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const t = e.changedTouches[0];
    return { x: (t.clientX - rect.left) * scaleX, y: (t.clientY - rect.top) * scaleY };
  };

  const startDrawing = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    e.preventDefault();
    const coords = getSignatureCoords(e);
    if (!coords) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    isDrawingRef.current = true;
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawingRef.current) return;
    const coords = getSignatureCoords(e);
    if (!coords) return;
    const ctx = signatureCanvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = (
    e?: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (e && "touches" in e && e.changedTouches?.length) {
      const coords = getSignatureCoordsEnd(e as React.TouchEvent<HTMLCanvasElement>);
      if (coords) {
        const ctx = signatureCanvasRef.current?.getContext("2d");
        if (ctx) {
          ctx.lineTo(coords.x, coords.y);
          ctx.stroke();
        }
      }
    }
    isDrawingRef.current = false;
  };

  const clearSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const confirmSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    setMemberInfo((prev) => ({ ...prev, signatureDataUrl: dataUrl }));
    setModalOpen(false);
  };

  const touchClass = isMobile ? " touch-manipulation" : "";
  const labelSize = isMobile ? "text-[13px] sm:text-sm" : "text-sm";

  const signatureModal = isModalOpen ? (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-[95vw]">
        <div className="flex items-center mb-4">
          <svg
            className="w-5 h-5 text-gray-400 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
            />
          </svg>
          <span className="text-gray-500 text-sm">Draw your signature</span>
        </div>
        <div className="border border-gray-200 rounded h-32 mb-4 bg-white overflow-hidden">
          <canvas
            ref={signatureCanvasRef}
            width={400}
            height={128}
            className="w-full h-32 block touch-none cursor-crosshair"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            onTouchCancel={stopDrawing}
          />
        </div>
        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => setModalOpen(false)}
            className={`text-gray-500 text-sm border-[1px] border-[#E9E9E9] rounded-lg px-4 py-2${touchClass}`}
          >
            Cancel
          </button>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={clearSignatureCanvas}
              className={`text-gray-500 text-sm border-[1px] border-[#E9E9E9] rounded-lg px-4 py-2${touchClass}`}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={confirmSignature}
              className={`text-gray-500 text-sm border-[1px] border-[#E9E9E9] rounded-lg px-4 py-2${touchClass}`}
            >
              Confirm Signature
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  if (hideTrigger) {
    return (
      <div data-testid="agreement-signature-section" data-variant={variant}>
        {signatureModal}
      </div>
    );
  }

  return (
    <div className="mt-6 sm:mt-8 w-full" data-testid="agreement-signature-section">
      {memberInfo.signatureDataUrl && memberInfo.signatureDataUrl.trim().startsWith("data:") ? (
        <div className="border border-[#E9E9E9] rounded-lg p-4 bg-[#FAFAFA]">
          <p className={`${labelSize} font-medium text-gray-700 mb-2`}>Your signature</p>
          <img
            src={memberInfo.signatureDataUrl}
            alt="Your signature"
            className="max-w-[200px] max-h-[80px] block mb-3"
          />
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className={`${labelSize} font-raleway font-medium text-primaryDark-200 hover:underline${touchClass}`}
          >
            Redraw / replace signature
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className={`w-full border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center hover:border-primaryDark-400 hover:bg-primaryDark-50 transition-colors${touchClass}`}
        >
          <div className="flex items-center justify-center mb-2">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
            <span className={`text-gray-500 ${labelSize}`}>Draw your signature</span>
          </div>
          <p className={`text-gray-400 ${isMobile ? "text-[12px] sm:text-xs" : "text-xs"}`}>
            Click to add signature
          </p>
        </button>
      )}

      {signatureModal}
    </div>
  );
};

export default AgreementSignatureSection;
